import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type {
    ApiResponse,
    PaginatedResponse,
    Course,
    ApiCourse,
    TrialFormData,
    ContactFormData,
    NewsletterFormData,
    User,
    Subscription,
    ApiError
} from '../types';
import { API_BASE_URL, API_ENDPOINTS } from '../data/constants';

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            // Handle unauthorized access
            localStorage.removeItem('authToken');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Transform Rails API course to frontend course format
const transformApiCourse = (apiCourse: ApiCourse): Course => {
    return {
        id: apiCourse.id.toString(),
        title: apiCourse.title,
        description: apiCourse.description,
        category: apiCourse.category as 'music' | 'yoga' | 'dance',
        icon: `/images/${apiCourse.category}-icon.webp`,
        features: [], // Will be populated from course details
        ageRange: apiCourse.age_range,
        price: apiCourse.price,
        currency: '₹',
        classCount: apiCourse.class_count,
        topics: apiCourse.topics,
        instructor: apiCourse.instructor,
        duration: apiCourse.duration,
        level: apiCourse.level as 'beginner' | 'intermediate' | 'advanced',
        isPopular: apiCourse.is_popular
    };
};

// Course API functions
export const courseApi = {
    // Get all courses with optional filters
    async getCourses(params?: {
        category?: string;
        level?: string;
        page?: number;
        limit?: number;
    }): Promise<Course[]> {
        try {
            const response = await apiClient.get<ApiResponse<ApiCourse[]>>(
                API_ENDPOINTS.courses,
                { params }
            );

            if (response.data.success && response.data.data) {
                return response.data.data.map(transformApiCourse);
            }
            throw new Error(response.data.message || 'Failed to fetch courses');
        } catch (error) {
            console.error('Error fetching courses:', error);
            throw error;
        }
    },

    // Get course by ID
    async getCourseById(id: string): Promise<Course> {
        try {
            const response = await apiClient.get<ApiResponse<ApiCourse>>(
                API_ENDPOINTS.courseById(id)
            );

            if (response.data.success && response.data.data) {
                return transformApiCourse(response.data.data);
            }
            throw new Error(response.data.message || 'Course not found');
        } catch (error) {
            console.error('Error fetching course:', error);
            throw error;
        }
    },

    // Get paginated courses
    async getPaginatedCourses(params?: {
        page?: number;
        limit?: number;
        category?: string;
        level?: string;
    }): Promise<PaginatedResponse<Course>> {
        try {
            const response = await apiClient.get<PaginatedResponse<ApiCourse>>(
                API_ENDPOINTS.courses,
                { params }
            );

            return {
                data: response.data.data.map(transformApiCourse),
                pagination: response.data.pagination
            };
        } catch (error) {
            console.error('Error fetching paginated courses:', error);
            throw error;
        }
    }
};

// Form submission API functions
export const formApi = {
    // Submit trial signup form
    async submitTrialForm(formData: TrialFormData): Promise<ApiResponse<any>> {
        try {
            const response = await apiClient.post<ApiResponse<any>>(
                API_ENDPOINTS.trialSignup,
                {
                    trial_signup: {
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone,
                        class_type: formData.classType,
                        age_group: formData.ageGroup,
                        experience: formData.experience,
                        preferred_time: formData.preferredTime,
                        message: formData.message
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error submitting trial form:', error);
            throw error;
        }
    },

    // Submit contact form
    async submitContactForm(formData: ContactFormData): Promise<ApiResponse<any>> {
        try {
            const response = await apiClient.post<ApiResponse<any>>(
                API_ENDPOINTS.contact,
                {
                    contact: {
                        name: formData.name,
                        email: formData.email,
                        subject: formData.subject,
                        message: formData.message
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error submitting contact form:', error);
            throw error;
        }
    },

    // Submit newsletter signup
    async submitNewsletterForm(formData: NewsletterFormData): Promise<ApiResponse<any>> {
        try {
            const response = await apiClient.post<ApiResponse<any>>(
                API_ENDPOINTS.newsletter,
                {
                    newsletter: {
                        email: formData.email
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error submitting newsletter form:', error);
            throw error;
        }
    }
};

// Authentication API functions
export const authApi = {
    // Login user
    async login(email: string, password: string): Promise<ApiResponse<{ user: User; token: string }>> {
        try {
            const response = await apiClient.post<ApiResponse<{ user: User; token: string }>>(
                API_ENDPOINTS.auth.login,
                {
                    auth: {
                        email,
                        password
                    }
                }
            );

            if (response.data.success && response.data.data?.token) {
                localStorage.setItem('authToken', response.data.data.token);
            }

            return response.data;
        } catch (error) {
            console.error('Error logging in:', error);
            throw error;
        }
    },

    // Register user
    async register(userData: {
        name: string;
        email: string;
        password: string;
        phone?: string;
    }): Promise<ApiResponse<{ user: User; token: string }>> {
        try {
            const response = await apiClient.post<ApiResponse<{ user: User; token: string }>>(
                API_ENDPOINTS.auth.register,
                {
                    user: userData
                }
            );

            if (response.data.success && response.data.data?.token) {
                localStorage.setItem('authToken', response.data.data.token);
            }

            return response.data;
        } catch (error) {
            console.error('Error registering:', error);
            throw error;
        }
    },

    // Logout user
    async logout(): Promise<void> {
        try {
            await apiClient.post(API_ENDPOINTS.auth.logout);
            localStorage.removeItem('authToken');
        } catch (error) {
            console.error('Error logging out:', error);
            // Still remove token even if API call fails
            localStorage.removeItem('authToken');
        }
    },

    // Get user profile
    async getProfile(): Promise<ApiResponse<User>> {
        try {
            const response = await apiClient.get<ApiResponse<User>>(
                API_ENDPOINTS.auth.profile
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching profile:', error);
            throw error;
        }
    }
};

// Subscription API functions
export const subscriptionApi = {
    // Get subscription plans
    async getPlans(): Promise<ApiResponse<any[]>> {
        try {
            const response = await apiClient.get<ApiResponse<any[]>>(
                API_ENDPOINTS.subscription.plans
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching plans:', error);
            throw error;
        }
    },

    // Subscribe to a plan
    async subscribe(planId: string, paymentMethodId?: string): Promise<ApiResponse<Subscription>> {
        try {
            const response = await apiClient.post<ApiResponse<Subscription>>(
                API_ENDPOINTS.subscription.subscribe,
                {
                    subscription: {
                        plan_id: planId,
                        payment_method_id: paymentMethodId
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error subscribing:', error);
            throw error;
        }
    },

    // Cancel subscription
    async cancel(subscriptionId: string): Promise<ApiResponse<any>> {
        try {
            const response = await apiClient.post<ApiResponse<any>>(
                API_ENDPOINTS.subscription.cancel,
                {
                    subscription: {
                        id: subscriptionId
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            throw error;
        }
    },

    // Update subscription
    async update(subscriptionId: string, planId: string): Promise<ApiResponse<Subscription>> {
        try {
            const response = await apiClient.post<ApiResponse<Subscription>>(
                API_ENDPOINTS.subscription.update,
                {
                    subscription: {
                        id: subscriptionId,
                        plan_id: planId
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating subscription:', error);
            throw error;
        }
    }
};

// Analytics API functions
export const analyticsApi = {
    // Track event
    async trackEvent(eventData: {
        event: string;
        category: string;
        action: string;
        label?: string;
        value?: number;
    }): Promise<void> {
        try {
            await apiClient.post('/analytics/events', {
                analytics_event: eventData
            });
        } catch (error) {
            console.error('Error tracking event:', error);
            // Don't throw error for analytics to avoid breaking user experience
        }
    },

    // Track page view
    async trackPageView(page: string, title?: string): Promise<void> {
        try {
            await apiClient.post('/analytics/page-views', {
                page_view: {
                    page,
                    title,
                    timestamp: new Date().toISOString()
                }
            });
        } catch (error) {
            console.error('Error tracking page view:', error);
            // Don't throw error for analytics
        }
    }
};

// Error handling utility
export const handleApiError = (error: any): ApiError => {
    if (error.response?.data?.errors) {
        return {
            message: error.response.data.errors[0] || 'An error occurred',
            code: error.response.status?.toString(),
            field: error.response.data.field
        };
    }

    if (error.response?.data?.message) {
        return {
            message: error.response.data.message,
            code: error.response.status?.toString()
        };
    }

    if (error.message) {
        return {
            message: error.message,
            code: 'NETWORK_ERROR'
        };
    }

    return {
        message: 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR'
    };
};

// Health check function
export const healthCheck = async (): Promise<boolean> => {
    try {
        const response = await apiClient.get('/health');
        return response.status === 200;
    } catch (error) {
        console.error('Health check failed:', error);
        return false;
    }
};

// Main API service object
export const apiService = {
    courses: courseApi,
    forms: formApi,
    auth: authApi,
    subscription: subscriptionApi,
    analytics: analyticsApi,
    submitTrialForm: formApi.submitTrialForm,
    submitContactForm: formApi.submitContactForm,
    submitNewsletterForm: formApi.submitNewsletterForm,
    healthCheck
};

// Export the configured axios instance for custom requests
export { apiClient };

// Default export with all API functions
export default apiService; 
